import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AdFormat } from "../api/contracts/site-config";
import { useConfiguration } from "../contexts/ConfigurationProvider";
import { getAdFormatNavigationSearch } from "../utils/urlParams";
import { useAdFormat } from "./useAdFormat";

/**
 * Pick an ad format, and go where it can be seen.
 *
 * The format only changes what the landing page renders, so choosing one from
 * the booking or checkout page would otherwise look like a dead control: the
 * chip moves and the page stays. Off the landing page the choice therefore
 * navigates to it, carrying the guest's dates and party across. The `/v2`
 * landing renders the same choice in place, so it is treated as home too.
 */
export const useSelectAdFormat = (): ((module: AdFormat) => void) => {
  const { selectFormat } = useAdFormat();
  const { identifier } = useConfiguration();
  const navigate = useNavigate();
  const location = useLocation();

  return useCallback(
    (module: AdFormat) => {
      selectFormat(module);

      if (!identifier) return;
      const landing = `/${identifier}`;
      // The plan (`/plan`, booking flow v2) is as much home as the landing: switching format
      // there must not throw the guest off it.
      if (
        location.pathname === landing ||
        location.pathname === `${landing}/plan`
      )
        return;

      navigate({
        pathname: landing,
        search: getAdFormatNavigationSearch(location.search, module),
      });
    },
    [selectFormat, identifier, location.pathname, location.search, navigate],
  );
};
